import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  writeBatch,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import {
  FileText,
  Download,
  GraduationCap,
  ChevronRight,
  Users,
  X,
} from 'lucide-react';

// Referrals page — practising lawyers see law-student resumes that the Jurident
// admin has shared with them, with the full internship-form details. Rendered
// inside the light CRM shell (PortalShell), so it uses the same light styling as
// the Shared Cases page — no dark background, navbar, footer, or background rings.
function Referrals() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();

  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (value) => {
    if (!value) return '';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const loadReferrals = useCallback(async () => {
    if (!globalUser) return;
    try {
      setIsLoading(true);

      let docs = [];
      try {
        const snap = await getDocs(
          query(
            collection(db, 'referrals'),
            where('lawyerId', '==', globalUser.uid),
            orderBy('sharedAt', 'desc'),
          ),
        );
        docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        // Fallback if the composite index isn't set up yet.
        console.warn('referrals query fell back:', err.message);
        const snap = await getDocs(
          query(collection(db, 'referrals'), where('lawyerId', '==', globalUser.uid)),
        );
        docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const at = a.sharedAt?.toMillis ? a.sharedAt.toMillis() : 0;
          const bt = b.sharedAt?.toMillis ? b.sharedAt.toMillis() : 0;
          return bt - at;
        });
      }

      // Hide referrals this lawyer has dismissed (removed for themselves).
      docs = docs.filter((r) => !r.dismissedByLawyer);
      setReferrals(docs);

      // Mark unseen referrals as seen so the dashboard badge clears.
      const unseen = docs.filter((r) => !r.seenByLawyer);
      if (unseen.length > 0) {
        const batch = writeBatch(db);
        unseen.forEach((r) => batch.update(doc(db, 'referrals', r.id), { seenByLawyer: true }));
        await batch.commit();
      }
    } catch (error) {
      console.error('Error loading referrals:', error);
      toast.error('Could not load referrals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [globalUser]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  // Remove a referral card for this lawyer only (keeps the underlying record).
  const handleDismiss = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    const previous = referrals;
    setReferrals((prev) => prev.filter((r) => r.id !== id));
    try {
      await updateDoc(doc(db, 'referrals', id), { dismissedByLawyer: true });
    } catch (err) {
      console.error('Error dismissing referral:', err);
      toast.error('Could not remove this referral. Please try again.');
      setReferrals(previous); // restore on failure
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Referrals</span>
        </div>

        {/* Header */}
        <div className="mt-2">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Referrals</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Law-student resumes shared with you by the Jurident team.
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-56 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && referrals.length === 0 && (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-20 text-center shadow-sm">
            <Users className="mb-4 h-14 w-14 text-slate-300" />
            <p className="text-lg font-medium text-slate-700">No referrals yet</p>
            <p className="mt-1 text-sm text-slate-500">
              When the Jurident team shares a student's resume with you, it appears here.
            </p>
            <button
              type="button"
              onClick={() => navigate('/portal/Dashboard')}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-yellow-500 px-5 py-2 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Cards */}
        {!isLoading && referrals.length > 0 && (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {referrals.map((r) => (
              <div
                key={r.id}
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yellow-50 text-yellow-500">
                      <GraduationCap className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold text-slate-900 leading-tight">
                        {r.studentName || r.preferredName || 'Law Student'}
                      </h3>
                      {r.studentEmail && (
                        <p className="truncate text-xs text-slate-500">{r.studentEmail}</p>
                      )}
                    </div>
                  </div>
                  {r.sharedAt && (
                    <span className="shrink-0 whitespace-nowrap text-[10px] text-slate-400">
                      {formatDate(r.sharedAt)}
                    </span>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                  <DetailRow label="Preferred Name" value={r.preferredName} />
                  <DetailRow label="Registration No." value={r.regNumber} />
                  <DetailRow label="Department" value={r.department} />
                  <DetailRow label="Field of Study" value={r.fieldOfStudy} />
                  <DetailRow label="CGPA" value={r.cgpa} />
                  <DetailRow label="Experience" value={r.experience} />
                  <DetailRow label="Permanent Address" value={r.permanentAddress} full />
                </div>

                {/* Footer: Cancel on the left, Resume on the right */}
                <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                  <button
                    type="button"
                    onClick={(e) => handleDismiss(e, r.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800"
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </button>

                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {r.coverLetterUrl && (
                      <a
                        href={r.coverLetterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        Cover Letter
                      </a>
                    )}
                    {r.resumeUrl ? (
                      <a
                        href={r.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md bg-yellow-500 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Resume
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">Resume not available</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function DetailRow({ label, value, full }) {
  if (!value) return null;
  return (
    <div className={full ? 'col-span-2' : ''}>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="whitespace-pre-line text-xs text-slate-700">{value}</p>
    </div>
  );
}

export default Referrals;
